// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
'use client';
// LoginPanel — écran de connexion UNIQUE de la suite BIO24. Le site et le manuel
// affichent exactement le même parcours (constat user : « je n'ai pas la même
// page de login selon que je demande l'espace personnel depuis le site ou depuis
// le manuel »). Volontairement autonome — état local + appels API — pour ne
// dépendre du store d'aucune app : c'est ce qui garantit qu'il n'y a qu'une
// seule implémentation à maintenir.
//
// Parcours : creds → [otp | enroll] → success → onSuccess(session).  Plus
// register (demande d'accès) et finalize (lien reçu par e-mail).
//
// L'étape « code » n'apparaît QUE si le laboratoire exige la double
// authentification (réglage du forfait). Elle est réelle : le mot de passe
// n'ouvre aucune session, le serveur garde le jeton en attente et seul un code
// TOTP valide le promeut en cookie de session. Ne jamais la réintroduire sans
// ce contrôle serveur derrière — c'était le vice de la version précédente.
//
// Routes appelées (mêmes chemins dans chaque app) :
//   POST /api/auth/login · POST /api/auth/2fa · GET /api/auth/me
//   POST /api/team/request-access · GET /api/team/invite/:token · POST /api/team/invite/accept
import React, { useEffect, useRef, useState } from 'react';
import { LOGIN_DICT, isRTL, LANGS, roleLabelOf, type Lang } from './loginDict';
import { aQuelqueChoseADire, libelleDuJour, type AccueilDuJour } from './accueilDuJour';

/* Ré-exporté : les applications hôtes écrivent le `chargerAccueil` qui rend ce
   type, et n'ont pas à connaître le module qui le définit. */
export type { AccueilDuJour };
import { useVerrouScroll } from './verrouScroll';
import { ChampTelephone } from './ChampTelephone';

// Règles d'affichage étroit, injectées par le composant lui-même pour que les
// trois apps aient EXACTEMENT le même comportement sans dépendre de leur
// feuille globale (constat d'Inès, 17/08 : panneau trop large sur mobile, le
// bandeau de marque écrasait le formulaire).
//  · le bandeau de marque disparaît — il n'apporte rien sur un petit écran ;
//  · `!important` sur la taille des champs : les styles sont posés en ligne
//    (15px), or en dessous de 16px iOS Safari zoome la page à la prise de
//    focus, et l'utilisateur se retrouve à déplacer un écran agrandi.
const LOGIN_CSS = `
@media (max-width: 720px) {
  /* !important obligatoire : le bandeau porte un display:flex EN LIGNE. */
  .login-row .login-brand { display: none !important; }
  .login-row input, .login-row select { font-size: 16px !important; }
}
`;

// `profile` (manuel, GED) et `role` (site) désignent la même chose : les routes
// /api/auth/me des trois apps ne nomment pas la clé pareil. On lit les deux.
export type LoginSession = { authenticated?: boolean; profile?: string; role?: string; name?: string; email?: string; apps?: Record<string, boolean> } & Record<string, unknown>;

export type LoginPanelProps = {
  /** Sous-titre du bandeau de marque (ex. « Manuel de prélèvement »). */
  moduleSub: string;
  lang: Lang;
  onLang: (l: Lang) => void;
  /** Appelé après la connexion, une fois l'écran de confirmation affiché. */
  onSuccess: (session: LoginSession) => void;
  asModal?: boolean;
  onClose?: () => void;
  /** « Consulter le catalogue public » — masqué si absent. */
  onPublic?: () => void;
  org?: { name?: string; addressLine?: string; referenceCountry?: string; countries?: string[] };
  /** Jeton du lien de finalisation reçu par e-mail (?finalize=…). */
  finalizeToken?: string | null;
  /** L'étape d'ouverture (19/10/2026) : « Demander un accès » depuis le
   *  catalogue public ouvre directement la demande, pas la connexion. */
  etapeInitiale?: 'creds' | 'register';
  /**
   * Charge le poste du jour, affiché SUR L'ÉCRAN DE RECONNAISSANCE (27/08/2026).
   *
   * ── Pourquoi une fonction et pas une valeur ─────────────────────────────
   *
   * La donnée vient d'une route RH que seule l'application Qualité sait
   * joindre. Les trois applications partagent ce panneau ; les deux autres ne
   * passent pas cette prop et l'écran reste EXACTEMENT ce qu'il était. Un
   * `fetch` écrit en dur ici y produirait un 404 silencieux à chaque
   * connexion.
   *
   * ⚠️ ELLE NE DOIT JAMAIS RETARDER L'ENTRÉE. Le minuteur d'entrée court dès
   * l'affichage ; si l'appel est lent ou échoue, on entre à l'heure sans le
   * mémo. Une politesse ne bloque pas une connexion.
   */
  chargerAccueil?: () => Promise<AccueilDuJour | null>;
};

/* ── L'HABILLAGE « PÂTE À MODELER » EST LE SEUL (27/09/2026) ─────────────────
 *
 * Il est arrivé le 14/09 derrière une prop `apparence="clay"`, que SEULE la
 * vitrine du catalogue passait : la même connexion avait donc deux visages
 * selon la porte empruntée — bandeau marine et citation depuis Qualité, le
 * site ou le catalogue historique ; bandeau clair cyan & vert tendre depuis la
 * vitrine. C'est le défaut que ce panneau partagé existe pour supprimer.
 *
 * ⚠️ L'ANCIEN BANDEAU MARINE EST RETIRÉ, pas désactivé. Un second habillage
 * que plus personne ne demande est du code qu'on croit vivant six mois plus
 * tard, et qui se remet en service par accident. Ce qu'il portait et qu'on ne
 * garde pas : la citation « Chaque échantillon est une personne… » et les
 * trois chiffres (24/7, 7 disciplines, ISO 15189) — ces deux derniers sont
 * dits par les pastilles du bandeau clair.
 *
 * Ce qui NE change pas : le parcours (identifiants, 2FA, inscription,
 * finalisation, mot de passe oublié) est exactement le même code. */

/* Feuille de l'habillage `clay`. `!important` : les champs et boutons du panneau
   portent leurs styles EN LIGNE. Bleu d'action #08628e + libellé blanc (6,7:1),
   survol éclairci #0a78ad (4,9:1) — les mêmes valeurs que le site /v3. */
const LOGIN_CLAY_CSS = `
.login-clay .login-form-wrap { background: #f7fcf6 !important; }
.login-clay input, .login-clay select, .login-clay textarea {
  background-color: #fff !important; border: 2px solid #0e2740 !important; border-radius: 14px !important; min-height: 48px;
}
.login-clay .login-primaire {
  background: #08628e !important; color: #fff !important; border: 3px solid #0e2740 !important; border-radius: 999px !important;
  box-shadow: 0 5px 0 rgba(14,39,64,.12), 0 12px 24px -12px rgba(14,39,64,.35) !important; height: 52px !important; font-weight: 800 !important;
  transition: transform .2s cubic-bezier(.34,1.56,.64,1), background .2s ease;
}
.login-clay .login-primaire:hover:not(:disabled) { background: #0a78ad !important; transform: translateY(-2px); }
.login-clay .login-langues { background: #fff !important; border: 2px solid #0e2740 !important; }
.login-clay .login-langues button[style*="var(--color-primary)"] { background: #08628e !important; color: #fff !important; }
.login-clay .login-fermer { width: 44px !important; height: 44px !important; border: 2px solid #0e2740 !important; color: #0e2740 !important; background: #fff !important; border-radius: 14px !important; }
.login-clay h2 { color: #0e2740 !important; font-weight: 800 !important; }
@media (prefers-reduced-motion: reduce) { .login-clay .login-primaire { transition: none; } .login-clay .login-primaire:hover { transform: none; } }
`;

/** Les textes du bandeau `clay` : des mots simples (lecteurs dont le français
 *  n'est pas la langue maternelle), le ton du site /v3. */
const CLAY_TEXTES = {
  FR: { surtitre: 'Espace personnel', titre: 'Content de vous revoir !', texte: 'Personnel du laboratoire ou laboratoire partenaire : connectez-vous pour ouvrir votre espace.', ouvert: 'Ouvert 24h/24 · 7j/7', iso: 'Démarche ISO 15189 en cours' },
  EN: { surtitre: 'Personal area', titre: 'Good to see you again!', texte: 'Laboratory staff or partner laboratory: sign in to open your area.', ouvert: 'Open 24/7', iso: 'ISO 15189 accreditation in progress' },
  AR: { surtitre: 'الفضاء الشخصي', titre: 'سعداء برؤيتك من جديد!', texte: 'موظفو المخبر أو المخابر الشريكة: سجّل الدخول لفتح فضائك.', ouvert: 'مفتوح 24/24 · 7/7', iso: 'مسار اعتماد ISO 15189 جارٍ' },
} as const;

/**
 * ⚠️ `sub` — LE NOM DU MODULE, ET IL N'EST PAS DÉCORATIF. Les trois
 * applications servent CE panneau : sans lui, « Qualité », « Catalogue des
 * analyses » et « Espace personnel » ouvrent un écran rigoureusement
 * identique, et l'on ne sait plus où l'on entre. Il est posé sous la marque,
 * à la place exacte qu'il occupait dans l'ancien bandeau marine.
 *
 * Il reste en FRANÇAIS dans les trois langues : c'est un nom de module, pas
 * une phrase — le traduire demanderait un dictionnaire par application, et
 * l'entête traduite juste en dessous porte déjà le ton.
 */
function BrandPanelClay({ lang, org, sub }: { lang: Lang; org?: LoginPanelProps['org']; sub: string }) {
  const x = CLAY_TEXTES[lang as keyof typeof CLAY_TEXTES] || CLAY_TEXTES.FR;
  const ar = lang === 'AR';
  const pastille: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 14px', borderRadius: 999, background: '#fff', border: '2px solid #0e2740', fontSize: 13.5, fontWeight: 800, color: '#0e2740', boxShadow: '0 4px 0 rgba(14,39,64,.12)' };
  return (
    <div className="login-brand" style={{ position: 'relative', flex: '0 0 46%', maxWidth: 580, color: '#0e2740', padding: '44px 48px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', overflow: 'hidden',
      background: 'radial-gradient(ellipse 80% 70% at 10% 5%, #e3f3fc 0%, transparent 60%), radial-gradient(ellipse 70% 60% at 95% 70%, #e6f3e0 0%, transparent 60%), #f7fcf6',
      borderInlineEnd: '3px solid #0e2740' }}>
      <div>
        <div style={{ fontFamily: ar ? undefined : 'var(--font-mono)', fontWeight: 700, fontSize: ar ? 18 : 15, lineHeight: 1.3 }}>
          {ar ? 'مخبر التحاليل الطبية' : lang === 'EN' ? 'Medical laboratory' : 'Laboratoire d’analyses médicales'}{' '}
          <span style={{ color: '#08628e', direction: 'ltr', unicodeBidi: 'isolate' }}>BiO24 Alliance</span>
        </div>
        <div style={{ marginTop: 6, fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em', color: '#08628e' }}>{sub}</div>
      </div>
      <div style={{ maxWidth: 420 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9, marginBottom: 16, fontSize: ar ? 15 : 13, fontWeight: 800, textTransform: ar ? 'none' : 'uppercase', letterSpacing: ar ? 0 : '0.1em', color: '#08628e' }}>
          <span style={{ width: 22, height: 3, borderRadius: 3, background: '#0e9fde', display: 'inline-block' }} />{x.surtitre}
        </div>
        <h1 style={{ margin: '0 0 16px', fontSize: 'clamp(30px, 3.4vw, 42px)', lineHeight: ar ? 1.35 : 1.1, fontWeight: 800, letterSpacing: ar ? 0 : '-0.025em', color: '#0e2740' }}>{x.titre}</h1>
        <p style={{ margin: 0, fontSize: 16, lineHeight: 1.6, color: '#41566b' }}>{x.texte}</p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ ...pastille, transform: `rotate(${ar ? 2 : -2}deg)` }}><span aria-hidden style={{ width: 9, height: 9, borderRadius: '50%', background: '#1f9e6a' }} />{x.ouvert}</span>
          <span style={{ ...pastille, background: '#e3f3fc', transform: `rotate(${ar ? -1.5 : 1.5}deg)` }}>{x.iso}</span>
        </div>
        {org?.addressLine && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: '#41566b' }}>
            <S d='<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>' size={15} sw={1.9} />
            {org.addressLine}
          </div>
        )}
      </div>
    </div>
  );
}


type Step = 'creds' | 'otp' | 'enroll' | 'success' | 'register' | 'registerDone' | 'finalize' | 'finalizeDone' | 'forgot' | 'forgotDone';
/**
 * LES TYPES DE CORRESPONDANT OUVERTS À L'INSCRIPTION (16/10/2026).
 * ⚠️ Même liste que `backend/src/correspondants.ts` (`TYPES_INSCRIPTION`) : le
 * socle ne peut pas l'importer, la suite `inscription-correspondant` les
 * confronte. Les libellés sont dans `loginDict` (trois langues).
 */
const TYPES_INSCRIPTION = [
  'confrere', 'clinique_conventionnee', 'centre_dialyse', 'medecin_prescripteur', 'preleveur_externe',
] as const;
/** Second facteur choisi par le laboratoire (réglage du forfait). `sms` et
 *  `whatsapp` sont déclarés par le serveur mais non retenus côté produit. */
type Mode2FA = 'google' | 'email' | 'sms' | 'whatsapp';

/** Délai anti-rafale du renvoi de code, en secondes. Aligné sur le refus 429 du
 *  moteur : le bouton ne doit pas inviter à une demande que le serveur rejette. */
const RESEND_DELAY_S = 60;

/** Interpolation minimale « {clé} » des libellés traduits. */
function fill(s: string, vars: Record<string, string | number>): string {
  return s.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? ''));
}

function S({ d, size = 16, color = 'currentColor', sw = 2 }: { d: string; size?: number; color?: string; sw?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: d }} />;
}

/** Œil « afficher / masquer le mot de passe ».
 *
 *  Défini AU NIVEAU DU MODULE, et non dans le corps du panneau : une fonction
 *  composant recréée à chaque rendu produit un type différent à chaque frappe,
 *  et React démonte puis remonte le bouton — le focus et l'infobulle sautent. */
function PwToggle({ visible, onClick }: { visible: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} aria-label="Afficher / masquer" type="button" style={{ position: 'absolute', insetInlineEnd: 6, top: '50%', transform: 'translateY(-50%)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, border: 'none', background: 'none', color: 'var(--text-muted)', cursor: 'pointer', borderRadius: 8 }}>
      {visible
        ? <S d='<path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" x2="22" y1="2" y2="22"/>' size={18} />
        : <S d='<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>' size={18} />}
    </button>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', fontFamily: 'var(--font-sans)', fontSize: 15, color: 'var(--text-strong)',
  // `backgroundColor` et NON le raccourci `background` : ce dernier
  // réinitialise `background-image`, donc efface le chevron que les feuilles de
  // style des apps dessinent sur les listes déroulantes. Le champ « pays » du
  // formulaire d'inscription se retrouvait sans flèche du tout.
  backgroundColor: 'var(--gray-25)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '12px 14px',
};
const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' };

function PrimaryBtn({ children, onClick, disabled, busy }: { children: React.ReactNode; onClick: () => void; disabled?: boolean; busy?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} className="login-primaire" style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', height: 48,
      fontFamily: 'var(--font-sans)', fontSize: 15, fontWeight: 600, color: '#fff',
      background: 'var(--color-primary)', border: 'none', borderRadius: 'var(--radius-md)',
      boxShadow: 'var(--shadow-brand)', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1,
    }}>
      {/* Témoin de chargement pendant l'appel réseau : sans lui, un bouton
          seulement grisé laissait croire à un gel (constat QA du 30/07). */}
      {busy && <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.45)', borderTopColor: '#fff', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />}
      {children}
    </button>
  );
}

function pwScore(pw: string): number {
  let s = 0;
  if (pw.length >= 8) s++;
  if (/\d/.test(pw)) s++;
  if ((/[a-z]/.test(pw) && /[A-Z]/.test(pw)) || /[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 3);
}
const emailOk = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

// profil serveur → clé d'affichage du rôle
function roleKeyOf(profile: string | undefined): string {
  switch (profile) {
    case 'superadmin': case 'admin': return 'admin';
    case 'manager': case 'responsable': return 'resp';
    case 'partner': case 'confrere': return 'partner';
    default: return 'tech';
  }
}

export function LoginPanel({ moduleSub, lang, onLang, onSuccess, asModal = false, onClose, onPublic, org, finalizeToken, chargerAccueil, etapeInitiale = 'creds' }: LoginPanelProps) {
  const t = LOGIN_DICT[lang] || LOGIN_DICT.FR;
  const dir = isRTL(lang) ? 'rtl' : 'ltr';
  const rtl = dir === 'rtl';
  // En modale, la page derrière ne doit plus défiler : c'est elle qui
  // « volait » le geste sur mobile et rendait le bouton d'envoi inatteignable.
  useVerrouScroll(asModal);
  // Champs saisis en écriture latine (identifiant, e-mail, téléphone) : le sens
  // de saisie reste LTR, mais en arabe le CONTENU s'aligne à droite comme le
  // reste du formulaire (retour d'Inès du 17/08 — cohérence visuelle).
  const alignRtl: React.CSSProperties = rtl ? { textAlign: 'right' } : {};

  const [step, setStep] = useState<Step>(etapeInitiale);
  const [error, setError] = useState<keyof typeof t | ''>('');
  // Message exact du moteur (« Code déjà utilisé — attendez le suivant. ») :
  // plus utile qu'un « code incorrect » générique quand l'utilisateur rejoue.
  const [serverMsg, setServerMsg] = useState('');
  /* ⚠️ `'forgot'` A DISPARU DE CETTE LISTE (12/09/2026) — c'était le défaut :
     un état d'information qui affichait « un lien a été envoyé » sans qu'aucun
     envoi n'ait eu lieu. La réinitialisation a désormais ses deux étapes. */
  const [info, setInfo] = useState<'codeSent' | ''>('');
  const [busy, setBusy] = useState(false);

  const [ident, setIdent] = useState('');
  const [password, setPassword] = useState('');
  const [pwVisible, setPwVisible] = useState(false);
  const [remember, setRemember] = useState(false);
  const [session, setSession] = useState<LoginSession | null>(null);

  // ---- double authentification ----
  const [otp, setOtp] = useState('');
  const [enrollData, setEnrollData] = useState<{ qr: string; secret: string } | null>(null);
  const otpRef = useRef<HTMLInputElement | null>(null);
  // Mode annoncé par /api/auth/login. Il décide de tout ce qui suit : en mode
  // `email` il n'y a AUCUN enrôlement — rien à configurer, le serveur envoie un
  // code à l'adresse du compte.
  const [mode2fa, setMode2fa] = useState<Mode2FA>('google');
  /** Adresse déjà masquée PAR LE SERVEUR (« in•••@exemple.tn »). Le client ne
   *  masque rien lui-même : il n'a jamais l'adresse en clair. */
  const [mailDest, setMailDest] = useState('');
  const [mailExpire, setMailExpire] = useState(10);
  const [resendIn, setResendIn] = useState(0);

  const refCountry = org?.referenceCountry || 'Tunisie';
  const countryList = (() => {
    const base = org?.countries?.length ? org.countries : ['Tunisie', 'Algérie', 'Maroc', 'Libye', 'France', 'Italie', 'Autre'];
    return base.includes(refCountry) ? base : [refCountry, ...base];
  })();

  /* Seuls les CORRESPONDANTS s'inscrivent ici (décision du 16/10/2026) : le
     personnel reçoit son accès depuis son dossier RH. D'où le type et la
     fonction, sans lesquels la validation ne peut pas relier le compte au
     carnet des correspondants. */
  const [rg, setRg] = useState({ typeCorrespondant: '', prenom: '', nom: '', institution: '', fonction: '', email: '', phone: '', city: '', country: refCountry });
  const [fz, setFz] = useState({ pw: '', pw2: '', phone: '', pwVisible: false });
  /* « Mot de passe oublié ? » — l'adresse saisie. Séparée de `ident` : celui-ci
     accepte un identifiant de connexion, et une réinitialisation part vers une
     ADRESSE. Les confondre enverrait un courriel à « admin.harzallah ». */
  const [fgEmail, setFgEmail] = useState('');
  /* Le dépôt a réussi, mais personne n'a pu être prévenu — un fait à dire, pas
     un échec à afficher en rouge. Vide dans le cas normal. */
  const [regAlerte, setRegAlerte] = useState('');
  /* L'accusé de dépôt (13/09/2026) est une PROMESSE faite à la personne :
     « regardez votre boîte ». On ne la fait donc qu'au vu du verdict du
     serveur, jamais par défaut — c'est la même règle que partout ailleurs
     ici : la phrase suit le fait, elle ne le devance pas. */
  const [regAccuse, setRegAccuse] = useState(false);
  const [invite, setInvite] = useState<{ token: string; prenom: string; nom: string; email: string } | null>(null);

  const errorMsg = serverMsg || (error ? (t[error] as string) : '');
  const infoMsg = info === 'codeSent' ? t.otpResentInfo : '';

  // Lien de finalisation reçu par e-mail : valide le jeton et bascule sur l'étape.
  const fzStarted = useRef(false);
  useEffect(() => {
    if (!finalizeToken || fzStarted.current) return;
    fzStarted.current = true;
    void (async () => {
      try {
        const r = await fetch(`/api/team/invite/${finalizeToken}`);
        if (!r.ok) return; // lien invalide/expiré → on reste sur la connexion
        const inv = await r.json();
        const parts = String(inv?.name || inv?.email || '').split(' ');
        setInvite({ token: finalizeToken, prenom: parts[0] || '', nom: parts.slice(1).join(' ') || '', email: inv?.email || '' });
        setStep('finalize');
      } catch { /* hors-ligne → on reste sur la connexion */ }
    })();
  }, [finalizeToken]);

  // Compte à rebours du « Renvoyer le code ». Un `setTimeout` d'une seconde
  // relancé à chaque décrément — et non un `setState` posé en tête d'effet, que
  // la règle react-hooks/set-state-in-effect interdit. L'horloge n'est jamais
  // lue pendant le rendu : seul ce compteur descend.
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  /* Le poste du jour, quand l'application hôte sait le fournir. Chargé
     seulement à l'écran de reconnaissance : avant, le cookie de session n'est
     pas encore posé (il l'est APRÈS le code à deux facteurs) et la route
     répondrait 401. */
  const [accueil, setAccueil] = useState<AccueilDuJour | null>(null);
  useEffect(() => {
    if (step !== 'success' || !chargerAccueil) return;
    let vivant = true;
    void chargerAccueil()
      /* Un refus ne montre RIEN plutôt qu'une erreur : le mémo est un agrément,
         pas une fonction. Le faire crier ferait passer une politesse pour une
         panne — c'est la règle déjà tenue par le mémo du tableau de bord. */
      .then((a) => { if (vivant && a) setAccueil(a); })
      .catch(() => {});
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Après connexion : afficher brièvement le rôle reconnu, puis entrer.
  //
  // ⚠️ LE MINUTEUR NE FAIT QUE REDÉMARRER, IL N'ATTEND RIEN. Il court dès
  // l'affichage sur 1,5 s — la durée d'origine. Si le mémo du jour arrive
  // avant, `accueil` change et le minuteur repart sur une durée de lecture :
  // il y a désormais une ligne à lire. S'il n'arrive pas, ou pas à temps, on
  // entre à l'heure. Aucun chemin ne peut donc allonger l'attente d'une
  // connexion à cause d'un appel réseau qui traîne.
  useEffect(() => {
    if (step !== 'success' || !session) return;
    const id = setTimeout(() => onSuccess(session), accueil ? 3400 : 1500);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, session, accueil]);

  const submitCreds = async () => {
    if (busy) return;
    if (!ident.trim() || !password.trim()) { setError('errReq'); setInfo(''); return; }
    setBusy(true); setError(''); setInfo(''); setServerMsg('');
    // Garde-fou navigateur : même si le proxy /api/auth/login pend (backend en
    // cours de redémarrage), on avorte au bout de 15 s → le `catch` réactive le
    // bouton et affiche l'erreur, au lieu de figer l'écran (constat QA bloquant
    // du 30/07). Le proxy borne déjà son propre appel à Strapi à 10 s ; ces 15 s
    // sont la seconde ligne de défense côté client.
    const ctrl = new AbortController();
    const timeoutId = setTimeout(() => ctrl.abort(), 15_000);
    try {
      const r = await fetch('/api/auth/login', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ identifier: ident.trim(), password }), signal: ctrl.signal,
      });
      // 401 = identifiants refusés ; toute autre erreur (500/502/503, timeout du
      // proxy) = service injoignable — deux messages distincts pour ne pas
      // accuser l'utilisateur d'un mauvais mot de passe quand le serveur est à
      // la peine.
      if (!r.ok) { setError(r.status === 401 ? 'badCreds' : 'server'); return; }
      const data = await r.json().catch(() => ({}));

      // Le laboratoire exige un deuxième facteur : le mot de passe seul n'ouvre
      // AUCUNE session. Le serveur garde le jeton en attente ; seul le code le
      // promeut. Ne rien appeler d'authentifié ici — il n'y a pas encore de
      // session.
      if (data?.need2fa) {
        setOtp('');
        const m = (String(data?.mode || 'google') as Mode2FA);
        setMode2fa(m);
        // Mode courriel : rien à enrôler — le serveur détient l'adresse et
        // envoie le code. On ne montre JAMAIS de QR ici ; il n'y a pas de
        // secret à partager avec une application.
        if (m === 'email') { await sendCode(true); return; }
        if (data.enrolled) { setStep('otp'); return; }
        await startEnroll();
        return;
      }

      // Même signal d'abandon que le login : si Strapi devient muet APRÈS le
      // login mais AVANT que /api/auth/me réponde (TCP accepté, pas de réponse —
      // ce n'est pas une erreur, donc `.catch` ne se déclencherait jamais), le
      // timeout des 15 s avorte aussi cet appel → on ne reste pas coincé dans le
      // `try` avec `busy` à true (auto-revue). Le login a réussi (cookie posé) :
      // en cas d'abandon, on entre avec la session minimale issue du login.
      const me: LoginSession | null = await fetch('/api/auth/me', { credentials: 'include', signal: ctrl.signal }).then((x) => (x.ok ? x.json() : null)).catch(() => null);
      const p = data?.user?.profile;
      setSession(me?.authenticated ? me : { authenticated: true, profile: p, role: p, name: data?.user?.name });
      setStep('success');
    } catch {
      setError('server');
    } finally {
      // Réactive TOUJOURS le bouton — c'est l'absence de ce filet (setBusy laissé
      // à true sur une requête qui ne revient jamais) qui figeait l'écran.
      clearTimeout(timeoutId);
      setBusy(false);
    }
  };

  /** Ouvre la session une fois le code validé par le serveur. */
  const finishLogin = async () => {
    const me: LoginSession | null = await fetch('/api/auth/me', { credentials: 'include' }).then((x) => (x.ok ? x.json() : null)).catch(() => null);
    setSession(me?.authenticated ? me : { authenticated: true });
    setStep('success');
  };

  /** Demande au serveur d'envoyer un code par courriel, puis affiche l'étape de
   *  saisie. `premier` distingue l'envoi automatique qui suit le mot de passe du
   *  renvoi demandé par l'utilisateur (seul ce dernier mérite un accusé). */
  const sendCode = async (premier: boolean) => {
    setBusy(true); setError(''); setServerMsg(''); setInfo('');
    try {
      const r = await fetch('/api/auth/2fa', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ action: 'send-code' }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        // 440 = le cookie d'attente a expiré : il faut refaire le mot de passe.
        if (r.status === 440) { setError('otpExpired'); setStep('creds'); return; }
        // 429 (« un code vient d'être envoyé ») et 502 (« le code n'a pas pu
        // être envoyé ») portent un message utile : on l'affiche tel quel. Sur
        // le premier envoi on bascule quand même sur l'étape du code — un code
        // précédent est peut-être encore valable, et l'écran offre le renvoi.
        const e = (d as { error?: { message?: string } | string } | null)?.error;
        const msg = typeof e === 'string' ? e : e?.message || '';
        setServerMsg(msg);
        if (!msg) setError('server');
        if (premier) { setResendIn(RESEND_DELAY_S); setStep('otp'); }
        return;
      }
      setMailDest(String((d as { destinataire?: string })?.destinataire || ''));
      setMailExpire(Number((d as { expireDans?: number })?.expireDans) || 10);
      setResendIn(RESEND_DELAY_S);
      if (!premier) { setOtp(''); setInfo('codeSent'); }
      setStep('otp');
    } catch {
      setError('server');
      if (premier) setStep('otp');
    } finally {
      setBusy(false);
    }
  };

  /** Prépare l'enrôlement (QR + clé). Le compte n'est pas encore protégé. */
  const startEnroll = async () => {
    setBusy(true); setError('');
    try {
      const r = await fetch('/api/auth/2fa', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ action: 'enroll' }),
      });
      if (!r.ok) { setError(r.status === 440 ? 'otpExpired' : 'server'); setStep('creds'); setBusy(false); return; }
      const d = await r.json();
      setEnrollData({ qr: d.qr, secret: d.secret });
      setStep('enroll'); setBusy(false);
    } catch { setError('server'); setBusy(false); }
  };

  /** `verify` (connexion) ou `confirm` (premier enrôlement) : dans les deux cas
   *  c'est le SERVEUR qui décide, et lui seul ouvre la session. */
  const submitOtp = async (action: 'verify' | 'confirm') => {
    if (busy) return;
    const code = otp.replace(/\D/g, '');
    if (code.length < 6) { setError('errOtp'); return; }
    setBusy(true); setError(''); setInfo('');
    try {
      const r = await fetch('/api/auth/2fa', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        // `remember` part ICI et nulle part ailleurs : le moteur ne rend un
        // jeton d'appareil qu'après un second facteur réussi. La case cochée à
        // l'écran du mot de passe n'aurait rien à quoi s'appliquer.
        body: JSON.stringify({ action, code, remember }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        if (r.status === 440) { setError('otpExpired'); setStep('creds'); setBusy(false); return; }
        setServerMsg(String(d?.error || '')); setError('errOtp'); setOtp(''); setBusy(false); return;
      }
      setBusy(false);
      await finishLogin();
    } catch { setError('server'); setBusy(false); }
  };

  /**
   * ⚠️ CE GESTE N'EXISTAIT PAS AVANT LE 12/09/2026.
   *
   * Le bouton « Mot de passe oublié ? » posait `setInfo('forgot')`, ce qui
   * affichait « Un lien de réinitialisation a été envoyé. » Il ne faisait AUCUN
   * appel réseau : aucun lien ne partait, à personne, jamais — que l'adresse
   * soit renseignée ou non. Signalement de la RQ du 11/09.
   *
   * ⚠️ LA PHRASE DE CONFIRMATION EST AU CONDITIONNEL, et ce n'est pas une
   * précaution de style : le moteur rend la MÊME réponse que le compte existe
   * ou non (voir `team.requestReset`), pour que ce formulaire public ne
   * devienne pas un moyen de savoir qui a un compte ici. L'écran ne peut donc
   * pas affirmer qu'un message est parti — et il ne doit pas le faire.
   */
  const submitForgot = async () => {
    if (busy) return;
    const adresse = fgEmail.trim();
    if (!adresse || !emailOk(adresse)) { setError('errRegEmail'); return; }
    setBusy(true);
    try {
      const r = await fetch('/api/team/request-reset', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: adresse }),
      });
      /* Le frein de débit répond 429 avec sa propre phrase, chiffrée : c'est
         une information utile (« réessayez dans X »), pas un échec à masquer. */
      if (!r.ok) {
        const d = await r.json().catch(() => null);
        setServerMsg(String(d?.error?.message || d?.error || '')); setError('server'); setBusy(false); return;
      }
    } catch { setError('server'); setBusy(false); return; }
    setStep('forgotDone'); setError(''); setServerMsg(''); setBusy(false);
  };

  const submitRegister = async () => {
    if (busy) return;
    if (!rg.typeCorrespondant || !rg.prenom.trim() || !rg.nom.trim() || !rg.email.trim() || !rg.phone.trim()
      || !rg.institution.trim() || !rg.fonction.trim() || !rg.city.trim() || !rg.country.trim()) { setError('errRegReq'); return; }
    if (!emailOk(rg.email.trim())) { setError('errRegEmail'); return; }
    setBusy(true);
    /* ⚠️ LA RÉPONSE EST LUE DEPUIS LE 12/09/2026.
     *
     * Ce bloc était un `try { await fetch(...) } catch { }` suivi d'un
     * `setStep('registerDone')` inconditionnel — et le commentaire assumait :
     * « best-effort : on confirme quand même à l'utilisateur ».
     *
     * Deux raisons pour lesquelles c'était intenable. D'abord un `fetch` qui
     * reçoit 404 ou 500 NE LÈVE PAS : le `catch` ne servait à rien, et l'écran
     * confirmait un échec franc du serveur. Ensuite c'est exactement ce qui
     * s'est produit — sur quality.bio24.tn, le proxy refusait
     * `request-access`, et la personne lisait « votre demande a été envoyée »
     * alors que rien n'était enregistré et que personne n'était prévenu.
     * (Seconde moitié du signalement de la RQ du 11/09.)
     *
     * ⚠️ `notifiee: false` N'EST PAS UN ÉCHEC. La demande est enregistrée ; ce
     * qui a manqué, c'est la notification interne. On confirme donc le dépôt —
     * mais en le disant, pour que la personne sache qu'il faudra relancer
     * autrement si personne ne la rappelle. */
    let alerte = '';
    let accuseOk = false;
    try {
      const r = await fetch('/api/team/request-access', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...rg, role: 'partner', email: rg.email.trim(),
          institution: rg.institution.trim(), fonction: rg.fonction.trim(),
          city: rg.city.trim(), country: rg.country.trim(),
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => null);
        setServerMsg(String(d?.error?.message || d?.error || '')); setError('server'); setBusy(false); return;
      }
      const d = await r.json().catch(() => null);
      if (d && d.notifiee === false) alerte = t.regDoneNonPrevenu;
      /* `=== true` et non « truthy » : un serveur qui ne renverrait pas encore
         ce champ (déploiement en cours, ancienne version) ne doit PAS faire
         promettre un courriel. L'absence d'information n'est pas un succès. */
      accuseOk = !!d && d.accuse === true;
    } catch { setError('server'); setBusy(false); return; }
    setRegAlerte(alerte);
    setRegAccuse(accuseOk);
    setStep('registerDone'); setError(''); setServerMsg(''); setBusy(false);
  };

  const submitFinalize = async () => {
    if (!invite || busy) return;
    if (fz.pw.length < 8) { setError('errFzPw'); return; }
    if (fz.pw !== fz.pw2) { setError('errFzMatch'); return; }
    if (!fz.phone.trim()) { setError('errFzPhone'); return; }
    setBusy(true);
    const name = `${invite.prenom} ${invite.nom}`.trim() || invite.email;
    const username = (invite.email.split('@')[0] || 'user').toLowerCase();
    try {
      const r = await fetch('/api/team/invite/accept', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: invite.token, name, username, password: fz.pw, phone: fz.phone.trim() }),
      });
      if (!r.ok) { setError('server'); setBusy(false); return; }
    } catch { setError('server'); setBusy(false); return; }
    setStep('finalizeDone'); setInvite(null); setError(''); setBusy(false);
  };


  // Champ du code, partagé par la vérification et l'enrôlement.
  const OtpField = (
    <input
      ref={otpRef} dir="ltr" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
      value={otp}
      onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); setServerMsg(''); }}
      placeholder="••••••"
      style={{
        ...inputStyle, textAlign: 'center', fontFamily: 'var(--font-mono)',
        fontSize: 28, fontWeight: 600, letterSpacing: '0.4em', padding: '14px',
      }}
    />
  );

  // « Se souvenir de cet appareil » — affiché aux étapes du CODE, et pas à
  // celle du mot de passe.
  //
  // C'est un déplacement volontaire : la case vivait sur l'écran des
  // identifiants, où elle ne pouvait rien promettre (elle n'était envoyée nulle
  // part, et sans second facteur il n'y a rien à ne pas redemander). Ici elle
  // dit vrai — cochée, elle voyage avec le code jusqu'au moteur, qui pose un
  // jeton d'appareil de 30 jours dispensant du SECOND FACTEUR seulement.
  const RememberBox = (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
        <span onClick={() => setRemember(!remember)} style={{ width: 18, height: 18, borderRadius: 5, flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: `1.5px solid ${remember ? 'var(--cyan-500)' : 'var(--border-strong)'}`, background: remember ? 'var(--cyan-500)' : 'transparent' }}>{remember && <S d='<polyline points="20 6 9 17 4 12"/>' size={13} color="#fff" sw={3} />}</span>
        <span onClick={() => setRemember(!remember)} style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-body)' }}>{t.remember}</span>
      </label>
      <div style={{ marginTop: 5, paddingInlineStart: 28, fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-muted)' }}>{t.rememberHelp}</div>
    </div>
  );

  const Err = errorMsg ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 16, padding: '10px 13px', borderRadius: 'var(--radius-sm)', background: 'var(--red-50)', border: '1px solid #F1C9C7', color: 'var(--red-600)', fontSize: 13, fontWeight: 500 }}>
      <S d='<circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/>' /> {errorMsg}
    </div>
  ) : null;

  const Info = infoMsg ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 16, padding: '10px 13px', borderRadius: 'var(--radius-sm)', background: 'var(--green-50)', border: '1px solid #BCE3D0', color: 'var(--green-600)', fontSize: 13, fontWeight: 500 }}>
      <S d='<polyline points="20 6 9 17 4 12"/>' sw={2.2} />{infoMsg}
    </div>
  ) : null;


  const fzScore = pwScore(fz.pw);
  const fzColor = ['var(--border-default)', 'var(--red-500)', 'var(--amber-500)', 'var(--green-500)'][fzScore];
  const fzLabel = ['', t.fzStrengthWeak, t.fzStrengthOk, t.fzStrengthStrong][fzScore];
  const fzInitials = ((invite?.prenom?.[0] || '') + (invite?.nom?.[0] || '')).toUpperCase() || '?';
  const roleKey = roleKeyOf(session?.profile ?? session?.role);

  return (
    <div className="login-row login-clay" style={{ position: 'relative', height: asModal ? '100%' : undefined, minHeight: asModal ? undefined : '100vh', display: 'flex', fontFamily: 'var(--font-sans)', color: 'var(--text-body)', background: 'var(--surface-card)' }}>
      <style dangerouslySetInnerHTML={{ __html: LOGIN_CSS }} />
      <style dangerouslySetInnerHTML={{ __html: LOGIN_CLAY_CSS }} />
      {asModal && onClose && (
        <button onClick={onClose} aria-label="Fermer" className="login-fermer" style={{ position: 'absolute', insetInlineEnd: 14, top: 14, zIndex: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: '50%', border: '1px solid var(--border-default)', background: 'var(--surface-card)', color: 'var(--text-muted)', cursor: 'pointer' }}>
          <S d='<path d="M18 6 6 18"/><path d="m6 6 12 12"/>' size={18} />
        </button>
      )}
      <BrandPanelClay lang={lang} org={org} sub={moduleSub} />

      <div className="login-form-wrap" style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--surface-card)' }}>
        {/* En modale, la croix de fermeture est ancrée en haut à droite : sans
            marge supplémentaire elle recouvre le « AR » du sélecteur de langue. */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: asModal ? '22px 60px 0 28px' : '22px 28px 0' }}>
          <div className="login-langues" style={{ display: 'inline-flex', alignItems: 'center', gap: 2, padding: 3, borderRadius: 'var(--radius-pill)', background: 'var(--gray-50)', border: '1px solid var(--border-default)' }}>
            {LANGS.map((l) => {
              const on = l === lang;
              return <button key={l} onClick={() => onLang(l)} style={{ fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 700, padding: '5px 11px', borderRadius: 'var(--radius-pill)', border: 'none', cursor: 'pointer', background: on ? 'var(--color-primary)' : 'transparent', color: on ? '#fff' : 'var(--text-muted)' }}>{l}</button>;
            })}
          </div>
        </div>

        {/* Colonne de contenu : elle défile ELLE-MÊME quand le formulaire est
            plus haut que la modale (l'inscription sur mobile). Le centrage se
            fait par `margin: auto` sur l'enfant et non `align-items: center` :
            centrer un enfant plus haut que son parent défilant en tronque le
            HAUT, hors d'atteinte — c'était l'autre moitié du bug mobile. */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', overflowY: 'auto', overscrollBehavior: 'contain', padding: '24px 32px' }}>
          <div dir={dir} style={{ width: '100%', maxWidth: 412, margin: 'auto' }}>

            {step === 'creds' && (
              <div>
                <div style={{ marginBottom: 24 }}>
                  <h2 style={{ margin: '0 0 8px', fontSize: 27, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>{t.title}</h2>
                  <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.55, color: 'var(--text-muted)' }}>{t.subtitle}</p>
                </div>
                {Err}
                {Info}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    <label style={labelStyle}>{t.identLabel}</label>
                    <input dir="ltr" type="text" value={ident} onChange={(e) => { setIdent(e.target.value); setError(''); }} placeholder={t.identPh} style={{ ...inputStyle, ...alignRtl }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    <label style={labelStyle}>{t.pwLabel}</label>
                    <div style={{ position: 'relative' }}>
                      <input dir="ltr" type={pwVisible ? 'text' : 'password'} value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }} placeholder="••••••••" style={{ ...inputStyle, padding: '12px 44px 12px 14px' }} onKeyDown={(e) => { if (e.key === 'Enter') void submitCreds(); }} />
                      <PwToggle visible={pwVisible} onClick={() => setPwVisible(!pwVisible)} />
                    </div>
                    {/* SOUS le mot de passe (Ines, 14/09/2026) : placé au-dessus,
                        il s'intercalait dans l'ordre de tabulation entre
                        l'identifiant et le mot de passe.
                        ⚠️ IL POSAIT `setInfo('forgot')`, ET RIEN D'AUTRE : la
                        phrase « Un lien de réinitialisation a été envoyé »
                        s'affichait sans qu'aucun appel ne parte. Il ouvre
                        désormais un formulaire, parce qu'une réinitialisation
                        a besoin d'une ADRESSE — le champ ci-dessus accepte
                        aussi un identifiant de connexion. */}
                    <button type="button" onClick={() => { setFgEmail(ident.includes('@') ? ident.trim() : ''); setStep('forgot'); setError(''); setServerMsg(''); }} style={{ alignSelf: 'flex-end', background: 'none', border: 'none', padding: 0, fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-link)', cursor: 'pointer' }}>{t.forgot}</button>
                  </div>
                  <PrimaryBtn onClick={submitCreds} disabled={busy} busy={busy}>
                    <S d='<rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>' size={16} />{t.continueBtn}
                  </PrimaryBtn>
                </div>
                <div style={{ marginTop: 22, paddingTop: 20, borderTop: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-muted)', maxWidth: 240 }}>{t.noAccount}</div>
                  <button onClick={() => { setStep('register'); setError(''); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 15px', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)', border: '1px solid var(--border-strong)', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', cursor: 'pointer' }}><S d='<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" x2="19" y1="8" y2="14"/><line x1="22" x2="16" y1="11" y2="11"/>' size={15} />{t.createAccount}</button>
                </div>
              </div>
            )}


            {step === 'otp' && (
              <div>
                <button onClick={() => { setStep('creds'); setOtp(''); setError(''); setServerMsg(''); setInfo(''); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 20, background: 'none', border: 'none', padding: 0, fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}><S d='<path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>' size={15} sw={2.2} />{t.otpBack}</button>
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, marginBottom: 16, borderRadius: 12, background: 'var(--cyan-50)', color: 'var(--cyan-600)' }}>
                  {mode2fa === 'email'
                    ? <S d='<path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/><path d="m16 19 2 2 4-4"/>' size={24} sw={1.9} />
                    : <S d='<rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>' size={24} sw={1.9} />}
                </div>
                <h2 style={{ margin: '0 0 8px', fontSize: 25, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>{t.otpTitle}</h2>
                {/* En mode courriel, on dit OÙ le code est parti — l'adresse est
                    déjà masquée par le serveur, le client ne l'a jamais en clair
                    — et combien de temps il reste valable. */}
                {mode2fa === 'email' ? (
                  <div style={{ margin: '0 0 20px' }}>
                    <p style={{ margin: '0 0 8px', fontSize: 14, lineHeight: 1.55, color: 'var(--text-muted)' }}>{t.otpMailSub}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 13px', borderRadius: 'var(--radius-md)', background: 'var(--gray-25)', border: '1px solid var(--border-default)' }}>
                      <S d='<path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>' size={15} sw={1.9} />
                      <span dir="ltr" style={{ fontFamily: 'var(--font-mono)', fontSize: 13.5, fontWeight: 600, color: 'var(--text-strong)', wordBreak: 'break-all' }}>{mailDest || '—'}</span>
                    </div>
                    <div style={{ marginTop: 7, fontSize: 12, color: 'var(--text-muted)' }}>{fill(t.otpMailValid, { min: mailExpire })}</div>
                  </div>
                ) : (
                  <p style={{ margin: '0 0 22px', fontSize: 14, lineHeight: 1.55, color: 'var(--text-muted)' }}>{t.otpSub}</p>
                )}
                {Err}
                {Info}
                <div style={{ marginBottom: 16 }}>{OtpField}</div>
                {mode2fa === 'email' && (
                  <div style={{ marginBottom: 14, textAlign: 'center' }}>
                    <button type="button" onClick={() => { if (resendIn === 0) void sendCode(false); }} disabled={busy || resendIn > 0}
                      style={{ background: 'none', border: 'none', padding: 0, fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, color: resendIn > 0 ? 'var(--text-faint)' : 'var(--text-link)', cursor: resendIn > 0 || busy ? 'default' : 'pointer' }}>
                      {resendIn > 0 ? fill(t.otpResendWait, { s: resendIn }) : t.otpResend}
                    </button>
                  </div>
                )}
                {RememberBox}
                <PrimaryBtn onClick={() => void submitOtp('verify')} disabled={busy || otp.length < 6} busy={busy}>{t.otpVerify}</PrimaryBtn>
              </div>
            )}

            {/* `mode2fa !== 'email'` en garde explicite : en mode courriel il
                n'existe aucun secret à enrôler, donc aucun QR à montrer. */}
            {step === 'enroll' && enrollData && mode2fa !== 'email' && (
              <div>
                <button onClick={() => { setStep('creds'); setOtp(''); setError(''); setServerMsg(''); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16, background: 'none', border: 'none', padding: 0, fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}><S d='<path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>' size={15} sw={2.2} />{t.otpBack}</button>
                <h2 style={{ margin: '0 0 6px', fontSize: 23, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>{t.enrollTitle}</h2>
                <p style={{ margin: '0 0 16px', fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-muted)' }}>{t.enrollSub}</p>
                {Err}
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 14, padding: 14, borderRadius: 'var(--radius-md)', background: 'var(--gray-25)', border: '1px solid var(--border-default)' }}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={enrollData.qr} alt="QR code d’enrôlement" width={120} height={120} style={{ flex: 'none', borderRadius: 6, background: '#fff' }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-body)' }}>{t.enrollScan}</div>
                    {/* Saisie manuelle : un poste sans caméra doit pouvoir s'enrôler. */}
                    <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--text-muted)' }}>{t.enrollManual}</div>
                    <code dir="ltr" style={{ display: 'block', marginTop: 4, fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-strong)', wordBreak: 'break-all', letterSpacing: '0.06em' }}>{enrollData.secret}</code>
                  </div>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginBottom: 8 }}>{t.enrollThen}</div>
                <div style={{ marginBottom: 14 }}>{OtpField}</div>
                {RememberBox}
                <PrimaryBtn onClick={() => void submitOtp('confirm')} disabled={busy || otp.length < 6} busy={busy}>{t.enrollConfirm}</PrimaryBtn>
              </div>
            )}

            {step === 'success' && (
              /* Colonne centrée : la pastille du rôle et le témoin de chargement
                 sont deux blocs `inline-flex` — en flux inline ils se plaçaient
                 côte à côte dès qu'ils tenaient sur la même ligne. */
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 68, height: 68, marginBottom: 22, borderRadius: '50%', background: 'var(--green-50)', color: 'var(--green-500)', animation: 'mp-pop 360ms var(--ease-out) both' }}><S d='<polyline points="20 6 9 17 4 12"/>' size={34} sw={2.4} /></div>
                <h2 style={{ margin: '0 0 8px', fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>
                  {accueil?.prenom ? (
                    /* ⚠️ `lang` et `dir` sur le mot arabe SEUL. Sans eux,
                       l'algorithme bidirectionnel colle la ponctuation
                       française du mauvais côté et le prénom passe parfois
                       AVANT le salut. Le prénom, lui, prend `dir="auto"` : il
                       est latin en français, arabe en arabe. */
                    <><span lang="ar" dir="rtl">عسلامة</span>{' '}<span dir="auto">{accueil.prenom}</span></>
                  ) : t.successTitle}
                </h2>
                <p style={{ margin: '0 0 22px', fontSize: 14.5, lineHeight: 1.55, color: 'var(--text-muted)' }}>{t.successSub}</p>
                {/* Deux pastilles côte à côte, qui passent l'une sous l'autre
                    à l'étroit : « ce que je suis » et « ce que je tiens
                    aujourd'hui ». La seconde n'apparaît que si la journée a
                    quelque chose à dire — voir `aQuelqueChoseADire`. */}
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10, marginBottom: 26 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 11, padding: '11px 18px', borderRadius: 'var(--radius-pill)', background: 'var(--gray-50)', border: '1px solid var(--border-default)' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 8, background: roleKey === 'admin' ? 'var(--navy-900)' : 'var(--cyan-50)', color: roleKey === 'admin' ? 'var(--cyan-300)' : 'var(--cyan-700)' }}>
                    <S d='<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>' size={17} />
                  </span>
                  <div style={{ textAlign: 'start' }}>
                    <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--text-muted)' }}>{t.roleDetected}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-strong)' }}>{roleLabelOf(t, roleKey)}</div>
                  </div>
                </div>
                {aQuelqueChoseADire(accueil) && accueil && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 11, padding: '11px 18px', borderRadius: 'var(--radius-pill)', background: 'var(--cyan-50)', border: '1px solid var(--border-default)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 8, background: '#fff', color: 'var(--cyan-700)' }}>
                      <S d='<circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14"/>' size={17} />
                    </span>
                    <div style={{ textAlign: 'start' }}>
                      <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--text-muted)' }}>{t.jourDetecte}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-strong)' }}>{libelleDuJour(accueil)}</div>
                    </div>
                  </div>
                )}
                </div>
                {/* Le témoin d'attente est CLIQUABLE : ajouter une ligne à
                    lire allonge la pause, et une pause qu'on ne peut pas
                    abréger devient vite un péage. Qui a déjà lu entre. */}
                <button type="button" onClick={() => { if (session) onSuccess(session); }}
                  title={t.enterNow}
                  /* ⚠️ `fontFamily` et NON le raccourci `font` : en style en
                     ligne, `font: 'inherit'` est écrit APRÈS et réinitialise la
                     taille et la graisse posées juste avant. */
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600, color: 'var(--text-muted)', background: 'none', border: 'none', padding: '4px 8px', cursor: 'pointer' }}>
                  <span style={{ width: 15, height: 15, borderRadius: '50%', border: '2px solid var(--border-default)', borderTopColor: 'var(--color-primary)', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                  {t.enterBtn}…
                </button>
              </div>
            )}

            {step === 'register' && (
              <div>
                <button onClick={() => { setStep('creds'); setError(''); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 18, background: 'none', border: 'none', padding: 0, fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}><S d='<path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>' size={15} sw={2.2} />{t.regBack}</button>
                <h2 style={{ margin: '0 0 8px', fontSize: 25, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>{t.regTitle}</h2>
                <p style={{ margin: '0 0 22px', fontSize: 14, lineHeight: 1.55, color: 'var(--text-muted)' }}>{t.regSub}</p>
                {Err}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {/* Le type en premier : c'est lui qui dit AU LABORATOIRE qui demande,
                      et c'est par lui que le compte sera relié au carnet. */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ ...labelStyle, fontSize: 12.5 }}>{t.regType}</label>
                    <select value={rg.typeCorrespondant} onChange={(e) => { setRg({ ...rg, typeCorrespondant: e.target.value }); setError(''); }} style={{ ...inputStyle, fontSize: 14.5, padding: '11px 34px 11px 13px' }}>
                      <option value="">{t.regTypePh}</option>
                      {TYPES_INSCRIPTION.map((c) => <option key={c} value={c}>{t.regTypes[c] || c}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                      <label style={{ ...labelStyle, fontSize: 12.5 }}>{t.regPrenom}</label>
                      <input type="text" value={rg.prenom} onChange={(e) => { setRg({ ...rg, prenom: e.target.value }); setError(''); }} placeholder={t.regPrenomPh} style={{ ...inputStyle, fontSize: 14.5, padding: '11px 13px' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                      <label style={{ ...labelStyle, fontSize: 12.5 }}>{t.regNom}</label>
                      <input type="text" value={rg.nom} onChange={(e) => { setRg({ ...rg, nom: e.target.value }); setError(''); }} placeholder={t.regNomPh} style={{ ...inputStyle, fontSize: 14.5, padding: '11px 13px' }} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                      <label style={{ ...labelStyle, fontSize: 12.5 }}>{t.regInstitution}</label>
                      <input type="text" value={rg.institution} onChange={(e) => { setRg({ ...rg, institution: e.target.value }); setError(''); }} placeholder={t.regInstitutionPh} style={{ ...inputStyle, fontSize: 14.5, padding: '11px 13px' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                      <label style={{ ...labelStyle, fontSize: 12.5 }}>{t.regFonction}</label>
                      <input type="text" value={rg.fonction} onChange={(e) => { setRg({ ...rg, fonction: e.target.value }); setError(''); }} placeholder={t.regFonctionPh} style={{ ...inputStyle, fontSize: 14.5, padding: '11px 13px' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ ...labelStyle, fontSize: 12.5 }}>{t.regEmail}</label>
                    <input dir="ltr" type="email" value={rg.email} onChange={(e) => { setRg({ ...rg, email: e.target.value }); setError(''); }} placeholder={t.regEmailPh} style={{ ...inputStyle, fontSize: 14.5, padding: '11px 13px', ...alignRtl }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ ...labelStyle, fontSize: 12.5 }}>{t.regPhone}</label>
                    <ChampTelephone value={rg.phone} onChange={(v) => { setRg({ ...rg, phone: v }); setError(''); }} ariaLabel={t.regPhone} style={{ ...inputStyle, letterSpacing: '0.04em', fontSize: 14.5, padding: '11px 13px' }} />
                  </div>
                  {(
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                        <label style={{ ...labelStyle, fontSize: 12.5 }}>{t.regCity}</label>
                        <input type="text" value={rg.city} onChange={(e) => { setRg({ ...rg, city: e.target.value }); setError(''); }} placeholder={t.regCityPh} style={{ ...inputStyle, fontSize: 14.5, padding: '11px 13px' }} />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                        <label style={{ ...labelStyle, fontSize: 12.5 }}>{t.regCountry}</label>
                        {/* Habillé par `inputStyle`, comme les champs texte qui
                            l'entourent, et NON par une classe : `.mp-select`
                            n'existe que dans le manuel. Dans les deux autres
                            modules ce champ tombait sur le rendu par défaut du
                            navigateur — angles droits et bordure d'un demi-pixel
                            au milieu de champs arrondis à 12 px (constaté sur
                            l'écran d'inscription de l'espace Qualité). */}
                        <select value={rg.country || refCountry} onChange={(e) => setRg({ ...rg, country: e.target.value })} style={{ ...inputStyle, fontSize: 14.5, padding: '11px 34px 11px 13px' }}>
                          {countryList.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                  <PrimaryBtn onClick={submitRegister} disabled={busy} busy={busy}>{t.regSubmit}</PrimaryBtn>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-muted)' }}><S d='<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>' size={14} />{t.regNote}</div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-muted)' }}><S d='<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>' size={14} />{t.regStaffNote}</div>
                </div>
              </div>
            )}

            {step === 'forgot' && (
              <div>
                <button onClick={() => { setStep('creds'); setError(''); setServerMsg(''); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 18, background: 'none', border: 'none', padding: 0, fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}><S d='<path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>' size={15} sw={2.2} />{t.regBack}</button>
                <h2 style={{ margin: '0 0 8px', fontSize: 25, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>{t.fgTitle}</h2>
                <p style={{ margin: '0 0 22px', fontSize: 14, lineHeight: 1.55, color: 'var(--text-muted)' }}>{t.fgSub}</p>
                {Err}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    <label style={labelStyle}>{t.fgEmail}</label>
                    <input dir="ltr" type="email" value={fgEmail} onChange={(e) => { setFgEmail(e.target.value); setError(''); }} placeholder={t.fgEmailPh} style={{ ...inputStyle, ...alignRtl }} onKeyDown={(e) => { if (e.key === 'Enter') void submitForgot(); }} />
                  </div>
                  <PrimaryBtn onClick={submitForgot} disabled={busy} busy={busy}>{t.fgBtn}</PrimaryBtn>
                </div>
              </div>
            )}

            {step === 'forgotDone' && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 68, height: 68, marginBottom: 22, borderRadius: '50%', background: 'var(--cyan-50)', color: 'var(--cyan-600)', animation: 'mp-pop 360ms var(--ease-out) both' }}><S d='<path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/><path d="m16 19 2 2 4-4"/>' size={32} sw={1.9} /></div>
                <h2 style={{ margin: '0 0 8px', fontSize: 25, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>{t.fgDoneTitle}</h2>
                {/* ⚠️ AU CONDITIONNEL. Le moteur rend la même réponse que le
                    compte existe ou non : l'écran ne PEUT pas affirmer qu'un
                    message est parti, et il ne doit pas le laisser croire. */}
                <p style={{ margin: '0 auto 22px', maxWidth: 340, fontSize: 14, lineHeight: 1.6, color: 'var(--text-muted)' }}>{t.fgDoneSub}</p>
                <PrimaryBtn onClick={() => { setStep('creds'); setFgEmail(''); }}>{t.fgDoneBtn}</PrimaryBtn>
              </div>
            )}

            {step === 'registerDone' && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 68, height: 68, marginBottom: 22, borderRadius: '50%', background: 'var(--cyan-50)', color: 'var(--cyan-600)', animation: 'mp-pop 360ms var(--ease-out) both' }}><S d='<path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/><path d="m16 19 2 2 4-4"/>' size={32} sw={1.9} /></div>
                <h2 style={{ margin: '0 0 8px', fontSize: 25, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>{t.regDoneTitle}</h2>
                <p style={{ margin: '0 auto 22px', maxWidth: 340, fontSize: 14, lineHeight: 1.6, color: 'var(--text-muted)' }}>{t.regDoneSub}</p>
                {/* Le dépôt a bien eu lieu ; c'est la notification interne qui
                    n'est pas partie. On le DIT, plutôt que de laisser attendre
                    un rappel que personne ne sait devoir passer. */}
                {!!regAlerte && (
                  <p style={{ margin: '0 auto 22px', maxWidth: 340, padding: '10px 13px', borderRadius: 'var(--radius-md)', background: 'var(--amber-50, #fffbeb)', border: '1px solid var(--amber-500, #F59E0B)', fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-body)', textAlign: 'start' }}>{regAlerte}</p>
                )}
                {/* L'accusé, lui, est une bonne nouvelle — et surtout une
                    CONSIGNE : la référence est dans ce courriel, c'est par
                    elle qu'on retrouvera la demande si la personne rappelle.
                    Affiché seulement si le serveur l'a confirmé parti. */}
                {regAccuse && (
                  <p style={{ margin: '0 auto 22px', maxWidth: 340, padding: '10px 13px', borderRadius: 'var(--radius-md)', background: 'var(--cyan-50)', border: '1px solid var(--cyan-200)', fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-body)', textAlign: 'start' }}>{t.regDoneAccuse}</p>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '0 auto 24px', maxWidth: 340, textAlign: 'start' }}>
                  {[t.regStep1, t.regStep2, t.regStep3].map((stepTxt, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 11, padding: '11px 14px', borderRadius: 'var(--radius-md)', background: 'var(--gray-25)', border: '1px solid var(--border-default)' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: 'var(--cyan-50)', color: 'var(--cyan-700)', fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700, flex: 'none' }}>{i + 1}</span>
                      <span style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-body)' }}>{stepTxt}</span>
                    </div>
                  ))}
                </div>
                <PrimaryBtn onClick={() => { if (onClose) onClose(); else setStep('creds'); }}>{t.regDoneBtn}</PrimaryBtn>
              </div>
            )}

            {step === 'finalize' && invite && (
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '6px 12px', borderRadius: 'var(--radius-pill)', background: 'var(--green-50)', color: 'var(--green-600)', fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}><S d='<polyline points="20 6 9 17 4 12"/>' size={14} sw={2.4} />{t.fzApproved}</div>
                <h2 style={{ margin: '0 0 8px', fontSize: 25, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>{t.fzTitle}</h2>
                <p style={{ margin: '0 0 20px', fontSize: 14, lineHeight: 1.55, color: 'var(--text-muted)' }}>{t.fzSub}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 20, padding: '11px 14px', borderRadius: 'var(--radius-md)', background: 'var(--gray-25)', border: '1px solid var(--border-default)' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: '50%', background: 'var(--cyan-50)', color: 'var(--cyan-700)', fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700, flex: 'none' }}>{fzInitials}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{invite.prenom} {invite.nom}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{invite.email}</div>
                  </div>
                </div>
                {Err}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ ...labelStyle, fontSize: 12.5 }}>{t.fzPw}</label>
                    <div style={{ position: 'relative' }}>
                      <input dir="ltr" type={fz.pwVisible ? 'text' : 'password'} value={fz.pw} onChange={(e) => { setFz({ ...fz, pw: e.target.value }); setError(''); }} placeholder={t.fzPwPh} style={{ ...inputStyle, fontSize: 14.5, padding: '11px 44px 11px 13px' }} />
                      <PwToggle visible={fz.pwVisible} onClick={() => setFz({ ...fz, pwVisible: !fz.pwVisible })} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 2 }}>
                      <span style={{ flex: 1, height: 5, borderRadius: 3, background: 'var(--gray-100)', overflow: 'hidden' }}><span style={{ display: 'block', height: '100%', width: `${(fzScore / 3) * 100}%`, background: fzColor, transition: 'all 200ms ease' }} /></span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: fzColor }}>{fzLabel}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ ...labelStyle, fontSize: 12.5 }}>{t.fzPw2}</label>
                    <input dir="ltr" type={fz.pwVisible ? 'text' : 'password'} value={fz.pw2} onChange={(e) => { setFz({ ...fz, pw2: e.target.value }); setError(''); }} placeholder={t.fzPwPh} style={{ ...inputStyle, fontSize: 14.5, padding: '11px 13px' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ ...labelStyle, fontSize: 12.5 }}>{t.fzPhone}</label>
                    <ChampTelephone value={fz.phone} onChange={(v) => { setFz({ ...fz, phone: v }); setError(''); }} ariaLabel={t.fzPhone} style={{ ...inputStyle, letterSpacing: '0.04em', fontSize: 14.5, padding: '11px 13px' }} />
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-muted)' }}><S d='<rect width="14" height="20" x="5" y="2" rx="2"/><path d="M12 18h.01"/>' size={14} />{t.fzPhoneNote}</div>
                  </div>
                  <PrimaryBtn onClick={submitFinalize} disabled={busy} busy={busy}>{t.fzSubmit}</PrimaryBtn>
                </div>
              </div>
            )}

            {step === 'finalizeDone' && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 68, height: 68, marginBottom: 22, borderRadius: '50%', background: 'var(--green-50)', color: 'var(--green-500)', animation: 'mp-pop 360ms var(--ease-out) both' }}><S d='<polyline points="20 6 9 17 4 12"/>' size={34} sw={2.4} /></div>
                <h2 style={{ margin: '0 0 8px', fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>{t.fzDoneTitle}</h2>
                <p style={{ margin: '0 auto 24px', maxWidth: 330, fontSize: 14.5, lineHeight: 1.55, color: 'var(--text-muted)' }}>{t.fzDoneSub}</p>
                <PrimaryBtn onClick={() => { setStep('creds'); setIdent(''); setPassword(''); setError(''); }}>{t.fzDoneBtn}</PrimaryBtn>
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '0 32px 28px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          {onPublic && step !== 'finalize' && step !== 'finalizeDone' && (
            <button onClick={onPublic} style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '11px 18px', border: '1px dashed var(--border-strong)', borderRadius: 'var(--radius-pill)', background: 'var(--surface-card)', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--text-body)', cursor: 'pointer' }}><S d='<circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/>' size={16} sw={1.9} />{t.publicLink}</button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, color: 'var(--text-faint)' }}><S d='<rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>' size={13} />{t.footer}</div>
        </div>
      </div>
    </div>
  );
}
